import { useMemo } from 'react';

import { Email, OrganizationSource } from '@alkem/lib-front-model';
import { getByLocale } from '@alkem/micro-frontend-tools';

import { TERMS_AND_CONDITIONS_LINK } from 'constants/links';
import leclercBackground from 'images/leclerc-background.jpg';
import leclercLogo from 'images/leclerc-logo.png';
import productDnaWhiteLogo from 'images/logo-productdna-white.png';
import productDnaLogo from 'images/logo-productdna.png';
import salsifyDarkLogo from 'images/logo-salsify-dark.png';
import salsifyLogo from 'images/logo-salsify.png';
import i18n from 'utils/i18n';

import { isLeclerc, isProductDNA } from '../utils';

interface BrandingConfigType {
  source: number;
  email: { onboarding: string; support: string };
  logo: {
    src: { login: string; loginDark: string; loginUK: string; navbar: string };
    alt: string;
    large?: boolean;
  };
  footer: {
    public: {
      links: { label: string; url: string }[];
      addon?: {
        title: string;
        background: string;
        primaryColor: string;
        secondaryColor: string;
        links: { title: string; url: string }[];
      };
    };
  };
  url: { tc: string };
  title?: string;
  loginExtraContent?: string;
}

export function useBrandingConfig(): BrandingConfigType {
  return useMemo(() => {
    if (isProductDNA()) {
      return getProductDnaConfig();
    } else if (isLeclerc()) {
      return getLeclercConfig();
    }
    return getAlkemicsConfig();
  }, []);
}

export function getAlkemicsConfig(): BrandingConfigType {
  return {
    source: OrganizationSource.ALKEMICS,
    email: {
      onboarding: Email.ONBOARDING,
      support: Email.SUPPORT,
    },
    logo: {
      src: {
        login: salsifyLogo,
        loginDark: salsifyDarkLogo,
        loginUK: salsifyDarkLogo,
        navbar: salsifyLogo,
      },
      alt: 'Salsify',
    },
    footer: {
      public: {
        links: [
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.about_link',
              {
                defaultValue: 'About',
              },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/about-salsify',
              fr: 'https://www.salsify.com/fr/a-propos-de-salsify',
            }),
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.press_link',
              {
                defaultValue: 'Press resources',
              },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/press-salsify',
              fr: 'https://www.salsify.com/fr/presse-salsify',
            }),
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.careers_link',
              { defaultValue: 'Careers' },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/careers',
              fr: 'https://www.salsify.com/fr/recrutement',
            }),
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.contact_link',
              { defaultValue: 'Contact' },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/en/contact-alkemics',
              fr: 'https://www.salsify.com/fr/contacter-alkemics',
            }),
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.general_conditions_link',
              { defaultValue: 'General conditions' },
            ),
            url: TERMS_AND_CONDITIONS_LINK,
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.privacy_link',
              { defaultValue: 'Privacy' },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/privacy-policy',
              fr: 'https://www.salsify.com/fr/confidentialit%C3%A9',
            }),
          },
        ],
      },
    },
    url: {
      tc: getByLocale({
        en: 'https://www.salsify.com/terms-of-service-2022-v1',
        fr: 'https://www.salsify.com/fr/terms-of-service-2022-v1',
      }),
    },
  };
}

export function getProductDnaConfig(): BrandingConfigType {
  const tcUrl =
    'https://www.gs1uk.org/~/media/documents/marketing-documents/gs1_uk_supplier_subscription_terms.pdf';
  return {
    source: OrganizationSource.PRODUCTDNA,
    email: {
      onboarding: 'productdna@gs1uk.org',
      support: 'support@gs1uk.org',
    },
    logo: {
      src: {
        login: productDnaLogo,
        loginDark: productDnaLogo,
        loginUK: productDnaLogo,
        navbar: productDnaWhiteLogo,
      },
      alt: 'ProductDNA',
    },
    footer: {
      public: {
        links: [
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.contact_link',
              { defaultValue: 'Contact' },
            ),
            url: 'mailto:productdna@gs1uk.org',
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.general_conditions_link',
              { defaultValue: 'General conditions' },
            ),
            url: tcUrl,
          },
        ],
      },
    },
    url: {
      tc: tcUrl,
    },
    title: 'productDNA',
  };
}

export function getLeclercConfig(): BrandingConfigType {
  return {
    ...getAlkemicsConfig(),

    logo: {
      src: {
        login: leclercLogo,
        loginDark: leclercLogo,
        loginUK: leclercLogo,
        navbar: salsifyLogo,
      },
      alt: 'Leclerc',
      large: true,
    },
    title: 'Leclerc',
    loginExtraContent: i18n.t(
      'frontproductstream.branding.leclerc.login_extra_content',
      { defaultValue: 'Alkemics is Leclerc RBA unique process tool' },
    ),
    footer: {
      public: {
        links: [
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.contact_link',
              { defaultValue: 'Contact' },
            ),
            url: getByLocale({
              en: 'https://www.salsify.com/en/contact-alkemics',
              fr: 'https://www.salsify.com/fr/contacter-alkemics',
            }),
          },
          {
            label: i18n.t(
              'frontproductstream.layout_public.footer.general_conditions_link',
              { defaultValue: 'General conditions' },
            ),
            url: TERMS_AND_CONDITIONS_LINK,
          },
        ],
        addon: {
          title: i18n.t('frontproductstream.branding.leclerc.help_title', {
            defaultValue: 'Need help?',
          }),
          background: leclercBackground,
          primaryColor: '#0a70b5',
          secondaryColor: '#ed8b18',
          links: [
            {
              title: i18n.t(
                'frontproductstream.branding.leclerc.training_link',
                { defaultValue: 'Subscribe to training' },
              ),
              url: 'https://onboarding.salsify.com/france/eleclerc',
            },
            {
              title: i18n.t(
                'frontproductstream.branding.leclerc.guide_book_link',
                { defaultValue: 'Download the guide book' },
              ),
              url: 'https://www.salsify.com/hubfs/SupplierXM/EMEA%20-%20Onboarding/Leclerc/@Leclerc%20RBA%20%23guide%20%23utilisateurs.pdf',
            },
          ],
        },
      },
    },
  };
}
